import { useState, useEffect, useCallback } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Typography,
  CircularProgress,
  Divider,
  List,
  ListItemButton,
  ListItemAvatar,
  Avatar,
  ListItemText,
  Alert,
  Chip,
} from "@mui/material";
import { useSnackbar } from "notistack";
import GenreSection from "./songs/GenreSection";
import UrlSection from "./songs/UrlSection";
import ImageUpload from "./songs/ImageUpload";

const API = process.env.REACT_APP_API_URL;

const ApproveRequestDialog = ({ open, request, onClose, onApproved }) => {
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(false);
  const [spotifySearching, setSpotifySearching] = useState(false);
  const [spotifyTracks, setSpotifyTracks] = useState([]);
  const [songData, setSongData] = useState({
    name: "",
    artist: "",
    album: "",
    image: "",
    release_year: "",
    decade: "",
    duration: "",
    spotify_id: "",
  });
  const [selectedGenres, setSelectedGenres] = useState([]);
  const [tempUrls, setTempUrls] = useState([]);
  const [songUrls, setSongUrls] = useState([]);

  const initFromRequest = useCallback((req) => {
    if (!req) return;

    const initialUrls = [];
    if (req.source === "youtube" && req.external_id) {
      initialUrls.push({
        song_url: `https://www.youtube.com/watch?v=${req.external_id}`,
        url_type_name: "Karaoke",
      });
    }
    if (req.source === "spotify" && req.external_id) {
      initialUrls.push({
        song_url: `https://open.spotify.com/track/${req.external_id}`,
        url_type_name: "Original",
      });
    }

    setSongData({
      name: req.title || "",
      artist: req.artist || "",
      album: "",
      image: req.thumbnail || "",
      release_year: "",
      decade: "",
      duration: "",
      spotify_id: req.source === "spotify" ? req.external_id : "",
    });
    setSelectedGenres([]);
    setTempUrls(initialUrls);
    setSongUrls([]);
    setSpotifyTracks([]);
  }, []);

  useEffect(() => {
    if (open && request) {
      initFromRequest(request);
    }
  }, [open, request, initFromRequest]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setSongData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSpotifySearch = async () => {
    if (!songData.name?.trim() || !songData.artist?.trim()) {
      enqueueSnackbar("Enter song name and artist to search Spotify", { variant: "warning" });
      return;
    }
    setSpotifySearching(true);
    try {
      const res = await fetch(
        `${API}/api/spotify/search-track?songName=${encodeURIComponent(songData.name)}&artistName=${encodeURIComponent(songData.artist)}`
      );
      const data = await res.json();
      if (!res.ok) {
        throw new Error(
          data.error ||
            (res.status === 503
              ? "Spotify is not configured on the server (.env.local)"
              : "Spotify search failed")
        );
      }
      setSpotifyTracks(data.tracks || []);
      if (!data.tracks?.length) {
        enqueueSnackbar("No Spotify tracks found", { variant: "info" });
      }
    } catch (err) {
      enqueueSnackbar(err.message || "Spotify search failed", { variant: "error" });
    } finally {
      setSpotifySearching(false);
    }
  };

  const applySpotifyTrack = (track) => {
    setSongData((prev) => ({
      ...prev,
      name: track.name || prev.name,
      artist: track.artist || prev.artist,
      album: track.album || prev.album,
      release_year: track.release_year || prev.release_year,
      decade: track.decade || prev.decade,
      duration: track.duration || prev.duration,
      image: track.image || prev.image,
      spotify_id: track.id || prev.spotify_id,
    }));

    const spotifyUrl = track.id
      ? `https://open.spotify.com/track/${track.id}`
      : null;
    if (spotifyUrl) {
      setTempUrls((prev) => {
        const without = prev.filter((u) => u.url_type_name !== "Original");
        return [
          ...without,
          { song_url: spotifyUrl, url_type_name: "Original" },
        ];
      });
    }
    setSpotifyTracks([]);
    enqueueSnackbar("Spotify track applied", { variant: "success" });
  };

  const handleSubmit = async () => {
    if (!request?.id) return;
    if (!songData.name?.trim() || !songData.artist?.trim()) {
      enqueueSnackbar("Song name and artist are required", { variant: "warning" });
      return;
    }

    setLoading(true);
    try {
      const payload = {
        name: songData.name.trim(),
        artist: songData.artist.trim(),
        album: songData.album || null,
        release_year: songData.release_year ? Number(songData.release_year) : null,
        decade: songData.decade || null,
        duration: songData.duration || null,
        image: songData.image || null,
        spotify_id: songData.spotify_id || null,
        genre_ids: selectedGenres.map((g) => g.genre_type_lookup_id),
        urls: tempUrls.map((u) => ({
          song_url: u.song_url,
          url_type_lookup_id: u.url_type_lookup_id,
          url_type_name: u.url_type_name,
        })),
      };

      const res = await fetch(
        `${API}/api/temp-song-requests/${request.id}/approve`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.message || data.error || "Approve failed");

      enqueueSnackbar(
        data.isDuplicate
          ? `Linked to existing song #${data.songs_master_id}`
          : `Added to catalog as song #${data.songs_master_id}`,
        { variant: "success" }
      );
      onApproved?.();
      onClose();
    } catch (err) {
      enqueueSnackbar(err.message || "Approve failed", { variant: "error" });
    } finally {
      setLoading(false);
    }
  };

  if (!request) return null;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ bgcolor: "#1a1a1a", color: "#fff" }}>
        Add to catalog — {request.title}
      </DialogTitle>
      <DialogContent sx={{ bgcolor: "#1a1a1a", color: "#fff" }}>
        <Alert severity="info" sx={{ mb: 2 }}>
          Set genres and links (Spotify / YouTube / Karaoke) before saving to{" "}
          <strong>songs_master</strong>, <strong>song_genres</strong>,{" "}
          <strong>song_urls</strong>, and <strong>song_artists</strong>.
        </Alert>

        <Box sx={{ display: "flex", gap: 1, mb: 2, alignItems: "center" }}>
          {request.thumbnail && (
            <Box
              component="img"
              src={request.thumbnail}
              alt=""
              sx={{ width: 64, height: 64, borderRadius: 1, objectFit: "cover" }}
            />
          )}
          <Box>
            <Typography variant="body2" color="text.secondary">
              User requests: <Chip size="small" label={request.request_count} />
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Source: {request.source} · ID: {request.external_id}
            </Typography>
          </Box>
        </Box>

        <Box display="flex" flexDirection="column" gap={2}>
          <TextField
            label="Song Name"
            name="name"
            value={songData.name}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label="Artist"
            name="artist"
            value={songData.artist}
            onChange={handleChange}
            fullWidth
            required
          />
          <TextField
            label="Album"
            name="album"
            value={songData.album}
            onChange={handleChange}
            fullWidth
          />
          <Box display="flex" gap={2}>
            <TextField
              label="Release Year"
              name="release_year"
              value={songData.release_year}
              onChange={handleChange}
              sx={{ flex: 1 }}
            />
            <TextField
              label="Decade"
              name="decade"
              value={songData.decade}
              onChange={handleChange}
              sx={{ flex: 1 }}
            />
          </Box>
          <TextField
            label="Duration"
            name="duration"
            value={songData.duration}
            onChange={handleChange}
            fullWidth
            placeholder="mm:ss"
          />

          <Divider />

          <Typography variant="subtitle2" sx={{ color: "#1DB954" }}>
            Spotify
          </Typography>
          <TextField
            label="Spotify Track ID"
            name="spotify_id"
            value={songData.spotify_id}
            onChange={handleChange}
            fullWidth
            helperText="Used in songs_master.spotify_id"
          />
          <Button
            variant="outlined"
            onClick={handleSpotifySearch}
            disabled={spotifySearching}
            sx={{ borderColor: "#1DB954", color: "#1DB954" }}
          >
            {spotifySearching ? <CircularProgress size={22} /> : "Search Spotify & apply metadata"}
          </Button>
          {spotifyTracks.length > 0 && (
            <List dense sx={{ bgcolor: "#121212", borderRadius: 1, maxHeight: 200, overflow: "auto" }}>
              {spotifyTracks.map((track) => (
                <ListItemButton key={track.id} onClick={() => applySpotifyTrack(track)}>
                  <ListItemAvatar>
                    <Avatar src={track.image} variant="rounded" />
                  </ListItemAvatar>
                  <ListItemText
                    primary={track.name}
                    secondary={`${track.artist} · ${track.album || ""}`}
                  />
                </ListItemButton>
              ))}
            </List>
          )}

          <GenreSection
            selectedGenres={selectedGenres}
            setSelectedGenres={setSelectedGenres}
          />

          <ImageUpload songData={songData} setSongData={setSongData} />

          <Divider />

          <Typography variant="subtitle2" sx={{ color: "#FFA726" }}>
            Song URLs (Karaoke, Original, Lyrics, Cover)
          </Typography>
          <UrlSection
            songUrls={songUrls}
            setSongUrls={setSongUrls}
            tempUrls={tempUrls}
            setTempUrls={setTempUrls}
            editingIndex={null}
            selectedSongId={null}
          />
        </Box>
      </DialogContent>
      <DialogActions sx={{ bgcolor: "#1a1a1a" }}>
        <Button onClick={onClose} disabled={loading}>
          Cancel
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={loading}
          sx={{ bgcolor: "#1DB954", color: "#000" }}
        >
          {loading ? <CircularProgress size={22} color="inherit" /> : "Save to catalog"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ApproveRequestDialog;
